// File: src/composables/useFocusTrap.ts
/**
 * Focus trap composable for modal accessibility.
 * Implements WCAG 2.1 focus management for dialogs.
 *
 * @remarks
 * - Cycles Tab / Shift+Tab within container.
 * - Returns focus to the invoker on deactivate.
 * - Redirects stray focus back into the trap (focusin guard).
 */
import { ref, nextTick, onMounted, onUnmounted, type Ref } from 'vue'

interface FocusTrapOptions {
    /** Element to return focus to on close (defaults to document.activeElement at activate time) */
    returnFocusTo?: HTMLElement | null
    /** CSS selector or element to focus first when activated */
    initialFocus?: HTMLElement | string | null
    /** Auto-activate trap on mount */
    immediate?: boolean
    /** Prevent body scroll while active (adds/removes `overflow:hidden` on <body>) */
    lockScroll?: boolean
}

interface FocusTrapReturn {
    /** Activate the focus trap */
    activate: () => Promise<void>
    /** Deactivate the focus trap and return focus */
    deactivate: () => void
    /** Whether the focus trap is active */
    isActive: Ref<boolean>
}

/** Global stack so only the top-most trap captures focusin */
const activeTrapStack: HTMLElement[] = []

/** Test if an element is truly focusable & visible */
const isFocusableVisible = (el: Element | null): el is HTMLElement => {
    if (!(el instanceof HTMLElement)) return false
    if (el.tabIndex < 0) return false // Exclude tabindex="-1" and below from tab order
    if (el.hasAttribute('disabled')) return false
    if (el.getAttribute('aria-hidden') === 'true') return false

    // Respect hidden/inert ancestors
    if (el.closest('[hidden], [aria-hidden="true"], [inert]')) return false

    const style = globalThis.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') return false
    if (!document.contains(el)) return false

    const rect = el.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return false

    return true
}

/**
 * Creates a focus trap for modal accessibility
 *
 * @param containerRef - Ref to the container element that should trap focus
 * @param options - Configuration options for the focus trap
 * @returns Focus trap controls and state
 */
export function useFocusTrap(
    containerRef: Ref<HTMLElement | null>,
    options: FocusTrapOptions = {}
): FocusTrapReturn {
    const isActive = ref(false)
    const { returnFocusTo, initialFocus, immediate = false, lockScroll = false } = options

    let lastFocusedElement: HTMLElement | null = null
    let previousTabIndex: string | null = null

    const getFocusableElements = (): HTMLElement[] => {
        const root = containerRef.value
        if (!root || !document.contains(root)) return []
        const selectors = [
            'a[href]',
            'area[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'details summary',
            '[contenteditable="true"]',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ')
        return Array.from(root.querySelectorAll(selectors)).filter(isFocusableVisible)
    }

    const focusFirst = (): void => {
        const list = getFocusableElements()
        list[0]?.focus()
    }

    const isTopMostTrap = (): boolean => {
        const root = containerRef.value
        return !!root && activeTrapStack[activeTrapStack.length - 1] === root
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (!isActive.value || event.key !== 'Tab' || !isTopMostTrap()) return
        const focusables = getFocusableElements()
        if (!focusables.length) return

        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement as HTMLElement | null

        if (event.shiftKey) {
            if (active === first || !containerRef.value?.contains(active)) {
                event.preventDefault()
                last.focus()
            }
        } else if (active === last || !containerRef.value?.contains(active)) {
            event.preventDefault()
            first.focus()
        }
    }

    /** Redirect any focus that slips outside back into the trap */
    const handleFocusIn = (event: FocusEvent): void => {
        if (!isActive.value || !isTopMostTrap()) return
        const target = event.target as HTMLElement | null
        if (!containerRef.value?.contains(target)) {
            focusFirst()
        }
    }

    const setupScrollLock = (): void => {
        if (lockScroll) {
            document.body.dataset.prevOverflow = document.body.style.overflow || ''
            document.body.style.overflow = 'hidden'
        }
    }

    const handleNoFocusables = (root: HTMLElement): void => {
        // Fallback: focus the container itself (preserve and restore prior tabindex)
        previousTabIndex = root.getAttribute('tabindex')
        root.setAttribute('tabindex', '-1')
        root.focus()
    }

    const isElementVisibleAndFocusable = (element: HTMLElement | null): boolean => {
        if (!element || !document.contains(element)) return false
        const style = globalThis.getComputedStyle(element)
        return style.display !== 'none' && style.visibility !== 'hidden'
    }

    const resolveInitialFocusElement = (): HTMLElement | null => {
        if (!initialFocus) return null

        if (typeof initialFocus === 'string') {
            const element = containerRef.value?.querySelector(initialFocus)
            return element instanceof HTMLElement ? element : null
        }

        return initialFocus
    }

    const findAutofocusElement = (focusables: HTMLElement[]): HTMLElement | null => {
        const auto = containerRef.value?.querySelector('[data-autofocus]')
        if (auto instanceof HTMLElement && focusables.includes(auto)) {
            return auto
        }
        return null
    }

    const determineInitialTarget = (focusables: HTMLElement[]): HTMLElement | null => {
        // Try initialFocus option first
        const target = resolveInitialFocusElement()
        if (target && isElementVisibleAndFocusable(target)) {
            return target
        }

        // Try [data-autofocus] element
        const autofocus = findAutofocusElement(focusables)
        if (autofocus) {
            return autofocus
        }

        // Fallback to first focusable element
        return focusables[0] ?? null
    }

    const activate = async (): Promise<void> => {
        const root = containerRef.value
        if (isActive.value || !root) return

        if (!document.contains(root)) {
            // Defensive: wait a tick if mount timing means root isn't in DOM yet
            await nextTick()
            if (!document.contains(root)) return
        }

        lastFocusedElement = (returnFocusTo ?? document.activeElement) as HTMLElement | null

        isActive.value = true
        activeTrapStack.push(root)
        document.addEventListener('keydown', handleKeyDown, true)
        document.addEventListener('focusin', handleFocusIn, true)

        setupScrollLock()

        await nextTick()

        const focusables = getFocusableElements()
        if (!focusables.length) {
            handleNoFocusables(root)
            return
        }

        const target = determineInitialTarget(focusables)
        target?.focus()

        if (!root.contains(document.activeElement)) {
            focusFirst()
        }
    }

    const removeFromTrapStack = (root: HTMLElement | null): void => {
        if (!root) return

        if (activeTrapStack[activeTrapStack.length - 1] === root) {
            activeTrapStack.pop()
        } else {
            // If not top-most (shouldn't happen often), remove the first match
            const idx = activeTrapStack.indexOf(root)
            if (idx >= 0) activeTrapStack.splice(idx, 1)
        }
    }

    const restoreScrollLock = (): void => {
        if (lockScroll) {
            document.body.style.overflow = document.body.dataset.prevOverflow ?? ''
            delete document.body.dataset.prevOverflow
        }
    }

    const restoreTabIndex = (root: HTMLElement | null): void => {
        if (root && previousTabIndex !== null) {
            // Restore original tabindex if we modified it
            if (previousTabIndex === '') root.removeAttribute('tabindex')
            else root.setAttribute('tabindex', previousTabIndex)
            previousTabIndex = null
        }
    }

    const restoreFocus = (): void => {
        if (lastFocusedElement && document.contains(lastFocusedElement)) {
            lastFocusedElement.focus()
        }
        lastFocusedElement = null
    }

    const deactivate = (): void => {
        if (!isActive.value) return
        isActive.value = false

        const root = containerRef.value
        removeFromTrapStack(root)

        document.removeEventListener('keydown', handleKeyDown, true)
        document.removeEventListener('focusin', handleFocusIn, true)

        restoreScrollLock()
        restoreTabIndex(root)
        restoreFocus()
    }

    onMounted(() => {
        if (immediate) {
            void activate()
        }
    })

    onUnmounted(() => {
        deactivate()
    })

    return { activate, deactivate, isActive }
}
